import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowDown, CheckCircle2, SquareArrowOutUpRight } from 'lucide-react'
import { FeeReceipt } from './fee-receipt'
import { RouteLine } from './route-line'
import { impactWarns } from '@/lib/price-impact'
import { type Flow, PositionProgress } from './position-progress'
import { useMe } from '@/lib/auth'
import type { PositionView } from '@/lib/api-types'
import { Modal } from './modal'
import { TradeSettings, useSlippageBps } from './trade-settings'
import { Spinner } from './spinner'
import { ProgressBar } from './progress-bar'
import { useToast } from './toast'
import { holdingToPicked, SOL_TOKEN, TokenPicker, type PickedToken } from './token-picker'
import { SOL_MINT, USDC_MINT, USDT_MINT } from '@/lib/default-tokens'
import { ChainChip, HeaderRule } from './header-chips'
import { errorMessage, useTRPC } from '@/lib/trpc'
import type { Holding, MeWallet } from '@/lib/api-types'
import { formatPct, formatTokenAmount, formatUsd } from '@/lib/format'
import { noteOwnActivity, useInvalidatePortfolio } from '@/lib/portfolio'
import { useRefreshActivity } from '@/lib/activity-feed'
import { isExchangeId, useCustody, usePoolMode, type ExchangeId } from '@/lib/pool'
import { PlatformGroup, usePlatformOptions } from './platform-chip'
import { useSetCustody } from '@/lib/custody'
import type { Chain } from '../../../src/constants/chains'

const SOLANA = 'solana' as Chain
const SOL_FEE_RESERVE = 0.005
type Phase =
  | { kind: 'form' }
  | { kind: 'quoting' | 'review' | 'sending' | 'done'; swapId: string }
  | { kind: 'position'; positionId: string; mode: 'open' | 'sell' }
/** SOL, USDC and USDT live in the trading wallet; every other token lives in the vault, bought and sold privately. */
const CORE_MINTS = new Set([SOL_MINT, USDC_MINT, USDT_MINT, SOL_TOKEN.address])

/** the last pair swapped, per browser, so the Swap button opens on it with the amounts empty (John, Nov 1 2026) */
const LAST_SWAP_KEY = 'dispence.swap.last'
type LastSwap = { pay: PickedToken; receive: PickedToken }
function readLastSwap(): LastSwap | null {
  try {
    const raw = localStorage.getItem(LAST_SWAP_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<LastSwap>
    return parsed.pay?.address && parsed.receive?.address ? (parsed as LastSwap) : null
  } catch {
    return null
  }
}
function saveLastSwap(pay: PickedToken, receive: PickedToken) {
  try {
    localStorage.setItem(LAST_SWAP_KEY, JSON.stringify({ pay: { ...pay, balance: 0 }, receive: { ...receive, balance: 0 } }))
  } catch {
    // a private window or blocked storage: nothing to remember
  }
}
const PRIVATE_MIN_SOL = 0.02
const PRIVATE_EXTRA_SOL = 0.01
/** a flow already running, opened from its chip in the in-progress bar (John, Oct 31 2026) */
export type TrackedSwap = { kind: 'position'; positionId: string; mode: 'open' | 'sell' } | { kind: 'swap'; swapId: string }

const trim = (v: number, d = 6) => v.toFixed(d).replace(/\.?0+$/, '')
function toRaw(value: string, decimals: number): bigint | null {
  const match = /^(\d*)(?:\.(\d*))?$/.exec(value.trim())
  if (!match || (match[1] === '' && !match[2])) return null
  const raw = BigInt((match[1] || '0') + (match[2] ?? '').slice(0, decimals).padEnd(decimals, '0'))
  return raw > 0n ? raw : null
}

export function SwapModal({
  open,
  onClose,
  wallet,
  holdings,
  initialPay = null,
  track = null,
}: {
  open: boolean
  onClose: () => void
  wallet: MeWallet
  holdings: Holding[]
  /** opened from a holdings row: that token pays and SOL receives; SOL itself leaves the receive side open */
  initialPay?: PickedToken | null
  /** opened from the in-progress bar: straight to that flow's progress, no form */
  track?: TrackedSwap | null
}) {
  const trpc = useTRPC()
  const { user: me } = useMe()
  const slippageBps = useSlippageBps()
  const toast = useToast()
  const invalidatePortfolio = useInvalidatePortfolio()
  const refreshActivity = useRefreshActivity()
  const tracking = track !== null
  // pool custody: SOL and the stables sit in the pool too, so every buy is a position and every sale comes out of the pool
  const poolMode = usePoolMode()
  const privateExtraSol = poolMode ? 0.015 : PRIVATE_EXTRA_SOL
  const solHolding = holdings.find((t) => t.isNative && t.chain === 'solana')
  const sol = useMemo(() => (solHolding ? holdingToPicked(solHolding) : SOL_TOKEN), [solHolding])
  const [pay, setPay] = useState<PickedToken | null>(null)
  const [receive, setReceive] = useState<PickedToken | null>(null)
  const [amount, setAmount] = useState('')
  const [debouncedRaw, setDebouncedRaw] = useState<string | null>(null)
  const [phase, setPhase] = useState<Phase>({ kind: 'form' })
  const [error, setError] = useState<string | null>(null)
  const [positionSettled, setPositionSettled] = useState(false)
  const [positionStatus, setPositionStatus] = useState<PositionView['status'] | null>(null)
  // a start that was refused shows as the flow's first step failing, not as a form error (John, Nov 1 2026)
  const [startFailed, setStartFailed] = useState(false)
  // the rail a private buy or a vault sale shows from the moment Swap is pressed: the same card then follows the record (John, Nov 1 2026: "one seamless progress UI")
  const [railFlow, setRailFlow] = useState<Flow | null>(null)
  useEffect(() => {
    if (open) {
      const fromToken = !!initialPay && !initialPay.isNative
      // opened from the top button: the last swap's pair comes back, amounts stay empty (John, Nov 1 2026)
      const last = initialPay ? null : readLastSwap()
      const fresh = (t: PickedToken) => {
        if (t.isNative) return sol
        const held = holdings.find((h) => h.chain === 'solana' && !h.isNative && h.address === t.address)
        return held ? holdingToPicked(held) : { ...t, balance: 0 }
      }
      setPay(fromToken ? initialPay : last ? fresh(last.pay) : sol)
      setReceive(fromToken ? sol : last ? fresh(last.receive) : null)
      setPhase(track ? (track.kind === 'position' ? { kind: 'position', positionId: track.positionId, mode: track.mode } : { kind: 'sending', swapId: track.swapId }) : { kind: 'form' })
      setAmount(''); setError(null); setPositionSettled(false); setPositionStatus(null); setStartFailed(false); setRailFlow(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, track])
  // keep the pay-side balance fresh after swaps without resetting the form
  useEffect(() => {
    if (pay?.isNative) setPay(sol)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sol.balance])

  const payDecimals = pay?.decimals ?? 9
  // the exchange this swap's SOL, USDC or USDT side uses (John, Oct 31 2026): the account's current one until the chip is clicked
  const { custody, exchange: onExchange } = useCustody()
  const [exchangeId, setExchangeId] = useState<ExchangeId>(isExchangeId(custody) ? custody : 'mexc')
  // the platform this swap's SOL, USDC or USDT side uses (John, Nov 1 2026): any of them, and the pick is remembered
  const platformOptions = usePlatformOptions()
  const { set: setCustody } = useSetCustody()
  const pickPlatform = (id: string) => {
    if (isExchangeId(id)) setExchangeId(id)
    setCustody.mutate({ custody: id as ExchangeId })
  }
  useEffect(() => {
    if (open && isExchangeId(custody)) setExchangeId(custody)
  }, [open, custody])
  const exchangeBalances = useQuery({ ...trpc.vault.exchangeBalances.queryOptions(), enabled: open && onExchange, refetchInterval: 15_000 })
  // balances come from the live holdings, so they move the moment a swap, deposit or withdrawal lands;
  // on the exchange side a core coin's balance is the chosen exchange's
  const liveBalance = (t: PickedToken | null) => {
    if (!t) return 0
    const ledger = onExchange ? exchangeBalances.data?.[exchangeId] : undefined
    const asset = t.isNative ? 'SOL' : t.address === USDC_MINT ? 'USDC' : t.address === USDT_MINT ? 'USDT' : null
    if (ledger && asset) return Number(ledger[asset]) / 10 ** (asset === 'SOL' ? 9 : 6)
    const h = holdings.find((x) => x.chain === 'solana' && (t.isNative ? x.isNative : x.address === t.address))
    return h ? Number(h.balance) / 10 ** h.decimals : (t.balance ?? 0)
  }
  const payHeld = liveBalance(pay)
  const receiveHeld = liveBalance(receive)
  const payBalance = pay ? Math.max(0, payHeld - (pay.isNative ? SOL_FEE_RESERVE : 0)) : 0
  const amountNumber = Number(amount) || 0
  // a buy pays SOL for a token; a sale pays a token for SOL, USDC or USDT, the proceeds landing at the exchange in that coin
  // (John, Nov 1 2026: "USDC and USDT, which every CEX supports, should be output options for proceeds")
  const receiveIsCoin = !!receive && (receive.isNative || receive.address === USDC_MINT || receive.address === USDT_MINT)
  const side: 'buy' | 'sell' | null = pay && receive ? (pay.isNative && !CORE_MINTS.has(receive.address) ? 'buy' : !CORE_MINTS.has(pay.address) && !pay.isNative && receiveIsCoin ? 'sell' : null) : null
  const tokenAddress = side === 'buy' ? receive?.address : side === 'sell' ? pay?.address : undefined
  const settlement: 'SOL' | 'USDC' | 'USDT' = receive?.address === USDC_MINT ? 'USDC' : receive?.address === USDT_MINT ? 'USDT' : 'SOL'

  useEffect(() => {
    const raw = toRaw(amount, payDecimals)
    const t = setTimeout(() => setDebouncedRaw(raw ? raw.toString() : null), 400)
    return () => clearTimeout(t)
  }, [amount, payDecimals])
  const estimate = useQuery({
    ...trpc.swap.estimate.queryOptions({ inputMint: pay?.address ?? '', outputMint: receive?.address ?? '', amountRaw: debouncedRaw ?? '0', slippageBps }),
    enabled: phase.kind === 'form' && !!pay && !!receive && debouncedRaw !== null && pay.address !== receive.address,
    staleTime: 10_000,
    refetchInterval: 15_000,
    retry: false,
  })
  const receiveDecimals = estimate.data?.outDecimals ?? receive?.decimals ?? undefined
  const estimatedOut = estimate.data && receiveDecimals !== undefined && receiveDecimals !== null ? Number(estimate.data.outAmountRaw) / 10 ** receiveDecimals : undefined
  // Dollar figures: SOL, USDC and USDT have reliable prices; a token is worth
  // what the quote says it is, anchored to the other side less the measured
  // impact, because a token's own price feed can lag its pool by 10% or more.
  const impactPct = estimate.data?.priceImpactPct
  const impactFraction = Math.max(0, Math.min(0.99, (impactPct ?? 0) / 100))
  const payIsCore = !!pay && (pay.isNative || CORE_MINTS.has(pay.address))
  const receiveIsCore = !!receive && (receive.isNative || CORE_MINTS.has(receive.address))
  const payUsdFeed = pay?.priceUsd ? amountNumber * pay.priceUsd : undefined
  const receiveUsdFeed = receive?.priceUsd && estimatedOut !== undefined ? estimatedOut * receive.priceUsd : undefined
  const payUsd = payIsCore || !receiveIsCore || receiveUsdFeed === undefined ? payUsdFeed : receiveUsdFeed / (1 - impactFraction)
  const receiveUsd = receiveIsCore || !payIsCore || payUsdFeed === undefined || estimatedOut === undefined ? receiveUsdFeed : payUsdFeed * (1 - impactFraction)

  // every start and stop reads the lists again at once, so the in-progress bar follows without waiting for a poll (John, Oct 31 2026)
  const create = useMutation(trpc.swap.create.mutationOptions({ onSuccess: (res) => { setPhase({ kind: 'quoting', swapId: res.swapId }); refreshActivity(['swap']) }, onError: (err) => setError(errorMessage(err)) }))
  const confirm = useMutation(trpc.swap.confirm.mutationOptions({ onSuccess: () => { setPhase((p) => (p.kind === 'review' ? { kind: 'sending', swapId: p.swapId } : p)); refreshActivity(['swap']) }, onError: (err) => setError(errorMessage(err)) }))
  const cancel = useMutation(trpc.swap.cancel.mutationOptions({ onSuccess: () => refreshActivity(['swap']) }))
  const openPosition = useMutation(trpc.position.open.mutationOptions({ onSuccess: (res) => { setPhase({ kind: 'position', positionId: res.id, mode: 'open' }); refreshActivity(['position']) }, onError: (err) => { setError(errorMessage(err)); setStartFailed(true) } }))
  const sellFromVault = useMutation(trpc.position.sellFromVault.mutationOptions({ onSuccess: (res) => { setPhase({ kind: 'position', positionId: res.id, mode: 'sell' }); refreshActivity(['position']) }, onError: (err) => { setError(errorMessage(err)); setStartFailed(true) } }))
  const swapId = phase.kind === 'form' || phase.kind === 'position' ? null : phase.swapId
  const status = useQuery({
    ...trpc.swap.status.queryOptions({ swapId: swapId ?? '' }),
    enabled: swapId !== null,
    refetchInterval: (q) => {
      const d = q.state.data
      if (d?.status === 'confirmed') return d.checked ? false : 1_500
      return d?.status === 'errored' || d?.status === 'cancelled' ? false : 1_500
    },
  })
  const s = status.data
  useEffect(() => {
    if (!s || phase.kind === 'form' || phase.kind === 'position') return
    if (phase.kind === 'quoting' && s.status === 'pendingUserConfirm' && s.estimatedOutputAmount) {
      if (s.warnings.length === 0) {
        // clean quote: no separate confirm step
        confirm.mutate({ swapId: phase.swapId })
        setPhase({ kind: 'sending', swapId: phase.swapId })
      } else {
        setPhase({ kind: 'review', swapId: phase.swapId })
      }
    } else if (s.status === 'confirmed' && phase.kind !== 'done') {
      setPhase({ kind: 'done', swapId: phase.swapId })
      const inSym = (s.inputTokenSymbol ?? '').toUpperCase()
      const outSym = (s.outputTokenSymbol ?? '').toUpperCase()
      // the bar's finished chip says so (John, Nov 1 2026: no separate snackbars)
      noteOwnActivity([inSym, outSym])
      void invalidatePortfolio()
    } else if (s.status === 'errored' || s.status === 'cancelled') {
      setError(s.error ?? (s.status === 'cancelled' ? 'Swap cancelled.' : 'Swap failed.'))
      setPhase({ kind: 'form' })
      if (s.status === 'errored') toast('error', 'Swap failed', s.error ?? undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s?.status, s?.estimatedOutputAmount])

  function flip() {
    const nextPay = receive
    const nextReceive = pay
    // the pay side needs a balance; look it up from holdings
    const held = nextPay ? holdings.find((t) => (t.isNative ? SOL_TOKEN.address : t.address) === nextPay.address) : undefined
    setPay(nextPay ? (held ? holdingToPicked(held) : { ...nextPay, balance: 0 }) : null)
    setReceive(nextReceive)
    setAmount('')
  }
  function startSwap() {
    setError(null)
    setStartFailed(false)
    setRailFlow(null)
    if (!pay || !receive) return setError('Pick what to pay and what to receive.')
    if (!side || !tokenAddress) return setError('Pay SOL for a token, or a token for SOL, USDC or USDT; token-to-token swaps are coming.')
    if (!(amountNumber > 0)) return setError('Enter an amount.')
    if (amountNumber > payBalance + 1e-12) return setError(pay.isNative ? 'Leave a little SOL for fees.' : 'Amount is more than your balance.')
    saveLastSwap(pay, receive)
    if (vaultSell) {
      const raw = toRaw(amount, payDecimals)
      if (!raw) return setError('Enter an amount.')
      setRailFlow('vaultSell')
      sellFromVault.mutate({ walletId: wallet.id, tokenAddress: pay.address, amountRaw: raw.toString(), slippageBps, exchange: onExchange ? exchangeId : undefined, settlement })
      return
    }
    if (privateBuy) {
      if (amountNumber < PRIVATE_MIN_SOL) return setError(`The smallest private buy is ${PRIVATE_MIN_SOL} SOL.`)
      if (amountNumber > payBalance - privateExtraSol + 1e-12) return setError(poolMode ? 'Leave about 0.02 SOL extra for the network fees.' : 'Leave about 0.01 SOL extra for the private routing and fees.')
      const raw = toRaw(amount, 9)
      if (!raw) return setError('Enter an amount.')
      // on an exchange the bought token stays in its own open wallet (the exchange takes no tokens); on Sonar's pool it is deposited
      setRailFlow(onExchange ? 'buy' : poolMode ? 'vaultBuy' : 'buy')
      openPosition.mutate({ walletId: wallet.id, tokenAddress, tokenSymbol: receive.symbol, amountLamports: raw.toString(), slippageBps, exchange: onExchange ? exchangeId : undefined })
      return
    }
    create.mutate({ chain: SOLANA, address: tokenAddress, side, amount: amount.trim(), amountUnit: side === 'buy' ? 'native' : 'token', walletId: wallet.id, clientRequestId: crypto.randomUUID(), quoteFirst: true })
  }
  function close() {
    if (phase.kind === 'review' || phase.kind === 'quoting') cancel.mutate({ swapId: phase.swapId })
    onClose()
  }

  const expiresIn = s?.expiresAt ? Math.max(0, Math.round((s.expiresAt - Date.now()) / 1000)) : null
  const busy = phase.kind === 'quoting' || phase.kind === 'sending' || phase.kind === 'review' || (phase.kind === 'position' && !positionSettled)
  // no toggles: a token buy always runs from a fresh wallet into the vault, a vault token always sells from a fresh wallet
  const privateBuy = side === 'buy' && !!receive && (poolMode ? receive.address !== SOL_MINT : !CORE_MINTS.has(receive.address))
  const vaultSell = side === 'sell' && pay?.custody === 'vault'
  const starting = create.isPending || openPosition.isPending || sellFromVault.isPending
  function reset() {
    setPhase({ kind: 'form' })
    setAmount('')
    setError(null)
    setStartFailed(false)
    setRailFlow(null)
    setPositionSettled(false)
    setPositionStatus(null)
  }
  // one card from the moment Swap is pressed to the end: it shows the start, then the record, on one rail (John, Nov 1 2026)
  const showRail = phase.kind === 'position' || (railFlow !== null && phase.kind === 'form')
  const finished = phase.kind === 'done' || (phase.kind === 'position' && positionSettled)
  const footer =
    tracking ? (
      // watching a flow that was started earlier: nothing to submit, only the progress to follow
      <button type="button" className={`btn w-full ${finished ? 'btn-primary' : 'btn-secondary'}`} onClick={onClose}>{finished ? 'Done' : 'Close'}</button>
    ) : phase.kind === 'review' ? (
      <div className="flex gap-3">
        <button type="button" className="btn btn-secondary flex-1" onClick={() => { cancel.mutate({ swapId: phase.swapId }); setPhase({ kind: 'form' }) }}>Cancel</button>
        <button type="button" className="btn btn-primary flex-1" disabled={confirm.isPending || expiresIn === 0} onClick={() => { confirm.mutate({ swapId: phase.swapId }); setPhase({ kind: 'sending', swapId: phase.swapId }) }}>
          {confirm.isPending ? <Spinner /> : expiresIn === 0 ? 'Quote expired' : 'Swap anyway'}
        </button>
      </div>
    ) : finished ? (
      <div className="flex gap-3">
        <button type="button" className="btn btn-secondary flex-1" onClick={reset}>Swap again</button>
        <button type="button" className="btn btn-primary flex-1" onClick={onClose}>Done</button>
      </div>
    ) : (
      <button type="button" className="btn btn-primary w-full" disabled={busy || starting} onClick={startSwap}>
        {phase.kind === 'quoting'
          ? 'Getting the best route…'
          : phase.kind === 'sending'
            ? 'Swapping…'
            : phase.kind === 'position'
              ? positionStatus === 'selling'
                ? 'Selling…'
                : positionStatus === 'funded' || positionStatus === 'buying'
                  ? 'Buying…'
                  : positionStatus === 'custodying' || positionStatus === 'returning'
                    ? onExchange ? 'Depositing…' : 'Returning…'
                    : 'Funding…'
              : starting
                ? 'Funding…'
                : 'Swap now'}
      </button>
    )

  return (
    <Modal
      open={open}
      onClose={close}
      title={
        <span className="flex items-center gap-3">
          {track?.kind === 'position' ? (track.mode === 'sell' ? 'Sell' : 'Buy') : 'Swap'}
          {/* the chain, in the header beside the title, then a rule and every exchange this swap's SOL, USDC or USDT side can use, the chosen one lit (John, Nov 1 2026: "move the CEX cycler to the top"; Nov 2 2026: "click to select ... rather than cycle", "a | or separator between those and the Sol chain") */}
          <ChainChip imageUrl={sol.imageUrl} />
          {!tracking && platformOptions.length > 0 ? (
            <>
              <HeaderRule />
              <PlatformGroup value={custody} options={platformOptions} disabled={setCustody.isPending} onChange={pickPlatform} />
            </>
          ) : null}
        </span>
      }
      footer={footer}
    >
      {/* The form stays on screen for the whole swap; progress shows underneath it. A flow opened from its chip shows the progress alone. */}
      <div className={`grid gap-2 *:min-w-0 ${busy ? 'pointer-events-none opacity-60' : ''} ${tracking ? 'hidden' : ''}`} aria-busy={busy} aria-hidden={tracking}>
        {/* You pay */}
        <div className="card grid gap-3 p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">You pay</span>
            <span className="flex items-center gap-2 text-xs text-accent">
              {[25, 50, 100].map((pct) => (
                <button key={pct} type="button" onClick={() => setAmount(trim((payBalance * pct) / 100, Math.min(payDecimals, 6)))}>{pct === 100 ? 'Max' : `${pct}%`}</button>
              ))}
              <TradeSettings />
            </span>
          </div>
          <div className="flex items-center gap-3">
            <input className="w-0 min-w-0 flex-1 bg-transparent text-3xl font-semibold outline-none placeholder:text-muted" inputMode="decimal" placeholder="0" value={amount} onChange={(e) => setAmount(e.target.value)} readOnly={busy} />
            <TokenPicker
              value={pay}
              onChange={(t) => {
                setPay(t)
                setAmount('')
                // selling a token: the other side is SOL unless the user already chose
                if (!t.isNative && (!receive || receive.address === t.address)) setReceive(sol)
                if (t.isNative && receive?.isNative) setReceive(null)
              }}
              holdings={holdings}
              allowSearch={false}
              heldOnly
              showSymbol
            />
          </div>
          <div className="flex items-center justify-between text-xs text-muted">
            <span>{payUsd !== undefined ? formatUsd(payUsd) : '$0.00'}</span>
            <span className="flex items-center gap-2">
              Balance {formatTokenAmount(payHeld, pay?.symbol)}
            </span>
          </div>
        </div>
        <div className="-my-4 flex justify-center">
          <button type="button" onClick={flip} className="z-10 flex size-9 items-center justify-center rounded-full border border-line bg-surface-2 text-muted transition-colors hover:text-text" title="Flip">
            <ArrowDown className="size-4" />
          </button>
        </div>
        {/* Receive */}
        <div className="card grid gap-3 p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Receive</span>
            {estimate.isFetching ? <Spinner className="size-4" /> : null}
          </div>
          <div className="flex items-center gap-3">
            <div className={`min-w-0 flex-1 truncate text-3xl font-semibold ${estimatedOut === undefined ? 'text-muted' : ''}`}>
              {estimatedOut !== undefined ? formatTokenAmount(estimatedOut, receive?.symbol) : '0'}
            </div>
            <TokenPicker value={receive} onChange={setReceive} holdings={holdings} allowSearch showSymbol />
          </div>
          {/* same footer row as You pay; the SOL/USDC/USDT shortcut chips that sat here are gone, the dropdown already lists them (John, Nov 2 2026) */}
          <div className="flex items-center justify-between text-xs text-muted">
            <span>{receiveUsd !== undefined ? formatUsd(receiveUsd) : '$0.00'}</span>
            {receive ? <span>Balance {formatTokenAmount(receiveHeld, receive.symbol)}</span> : null}
          </div>
        </div>
        {phase.kind === 'form' && impactWarns(impactPct, payUsd) ? (
          <p className="text-xs text-warning">Price impact {formatPct(impactPct, false)} on this size.</p>
        ) : null}
        {phase.kind === 'form' && estimate.isError ? <p className="text-xs text-negative">No route found for this pair and size.</p> : null}
      </div>

      {/* Progress, in place */}
      {phase.kind === 'quoting' ? (
        <div className="card mt-3 grid gap-3 p-4 text-sm"><ProgressBar value={30} /><div><div className="font-medium">Getting the best route…</div><div className="text-xs text-muted">Clean quotes swap straight away; risky ones stop here for a look.</div></div></div>
      ) : phase.kind === 'review' && s ? (
        <div className="card mt-3 grid gap-3 p-4 text-sm">
          <div className="rounded-control border border-warning/40 bg-warning/10 p-3">
            <div className="mb-1 font-medium text-warning">Check before swapping</div>
            <ul className="grid gap-1 text-xs text-warning">{s.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
          </div>
          <Row label="You pay" value={`${formatTokenAmount(s.inputAmount?.uiAmount ?? 0, s.inputTokenSymbol)} ${s.inputTokenSymbol ?? ''}${s.inputAmount?.usdValue ? ` (${formatUsd(s.inputAmount.usdValue)})` : ''}`} />
          <Row label="You receive (est.)" value={`${formatTokenAmount(s.estimatedOutputAmount?.uiAmount ?? 0, s.outputTokenSymbol)} ${s.outputTokenSymbol ?? ''}${s.estimatedOutputAmount?.usdValue ? ` (${formatUsd(s.estimatedOutputAmount.usdValue)})` : ''}`} />
          <Row label="Price impact" value={<span className={impactWarns(s.priceImpactPct, s.inputAmount?.usdValue) ? 'text-warning' : ''}>{formatPct(s.priceImpactPct, false)}</span>} />
          <Row label="Network fee" value={s.networkFeeUsd !== undefined ? formatUsd(s.networkFeeUsd) : '–'} />
          <Row label="Slippage" value={s.slippageBps !== undefined ? `${(s.slippageBps / 100).toFixed(2)}%` : '–'} />
          {expiresIn !== null ? <p className="text-xs text-muted">Quote valid for {expiresIn}s.</p> : null}
        </div>
      ) : phase.kind === 'sending' ? (
        <div className="card mt-3 grid gap-3 p-4 text-sm"><ProgressBar value={s?.transactionId ? 85 : 65} /><div><div className="font-medium">Swapping…</div><div className="text-xs text-muted">{s?.transactionId ? 'Transaction sent, waiting for confirmation.' : 'Signing and sending through Jupiter.'}</div></div></div>
      ) : phase.kind === 'done' ? (
        <div className="card mt-3 flex items-center gap-3 p-4 text-sm">
          <CheckCircle2 className="size-6 shrink-0 text-accent" />
          <div className="min-w-0 flex-1">
            <div className="font-medium">Swapped {formatTokenAmount(s?.inputAmount?.uiAmount ?? 0, s?.inputTokenSymbol)} {s?.inputTokenSymbol} → {s?.outputAmount ? '' : '≈'}{formatTokenAmount((s?.outputAmount ?? s?.estimatedOutputAmount)?.uiAmount ?? 0, s?.outputTokenSymbol)} {s?.outputTokenSymbol}</div>
            {s && !s.checked ? <div className="text-xs text-muted">Confirming the final amount…</div> : null}
            <RouteLine route={s?.route} className="mt-2" />
            <FeeReceipt items={s?.fees} className="mt-2" />
          </div>
          {s?.transactionId ? <a className="btn btn-ghost !px-2 !py-1 text-xs" href={`https://solscan.io/tx/${s.transactionId}`} target="_blank" rel="noreferrer">View txn <SquareArrowOutUpRight className="size-3.5" /></a> : null}
        </div>
      ) : showRail ? (
        <div className="card mt-3 p-4">
          <PositionProgress
            positionId={phase.kind === 'position' ? phase.positionId : null}
            mode={phase.kind === 'position' ? phase.mode : railFlow === 'vaultSell' ? 'sell' : 'open'}
            flow={railFlow ?? undefined}
            exchange={onExchange}
            house={onExchange && (me?.presets.removeInsiderMark ?? true)}
            starting={{ failed: startFailed, error, onBack: () => { setStartFailed(false); setError(null); setRailFlow(null) } }}
            onSettled={() => setPositionSettled(true)}
            onStatus={setPositionStatus}
          />
        </div>
      ) : null}
      {error && !showRail ? <p className="mt-2 text-sm text-negative">{error}</p> : null}
    </Modal>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return <div className="flex items-start justify-between gap-4"><span className="text-muted">{label}</span><span className="text-right">{value}</span></div>
}
